import React, { useState, useRef } from 'react';

const SwpCalculatorSection = () => {
  const [initialInvestment, setInitialInvestment] = useState(2000000);
  const [monthlyWithdrawal, setMonthlyWithdrawal] = useState(15000);
  const [expectedReturn, setExpectedReturn] = useState(12);
  const [result, setResult] = useState(null);
  const [chartData, setChartData] = useState(null);
  const resultRef = useRef(null);

  const calculateSWP = () => {
    const monthlyRate = expectedReturn / 100 / 12;
    let remainingAmount = initialInvestment;
    const monthlyData = [];
    let totalWithdrawn = 0;
    let month = 0;
    
    // Calculate month by month until fund is exhausted or 30 years (360 months)
    while (remainingAmount > 0 && month < 360) {
      month++;
      
      // Add monthly return
      remainingAmount = remainingAmount * (1 + monthlyRate);
      
      // Subtract monthly withdrawal
      const withdrawal = Math.min(monthlyWithdrawal, remainingAmount);
      remainingAmount -= withdrawal;
      totalWithdrawn += withdrawal;
      
      // Store data for chart (every 12 months for yearly data)
      if (month % 12 === 0) {
        monthlyData.push({
          year: month / 12,
          remaining: Math.max(0, remainingAmount),
          withdrawn: totalWithdrawn
        });
      }
    }
    
    const yearsLasted = month / 12;
    const finalAmount = Math.max(0, remainingAmount);
    
    setChartData(monthlyData);
    setResult({
      yearsLasted: yearsLasted.toFixed(1),
      totalWithdrawn: Math.round(totalWithdrawn),
      finalAmount: Math.round(finalAmount),
      monthsLasted: month
    });

    // Scroll to results section
    setTimeout(() => {
      resultRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const styles = {
    swpCalculatorContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2rem'
    },
    description: {
      color: '#6b7280',
      fontSize: '1rem',
      lineHeight: '1.6',
      margin: '0 0 2rem 0'
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem'
    },
    label: {
      fontSize: '0.95rem',
      fontWeight: '600',
      color: '#374151',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    valueDisplay: {
      backgroundColor: '#374151',
      color: 'white',
      padding: '0.25rem 0.75rem',
      borderRadius: '15px',
      fontSize: '0.85rem',
      fontWeight: '600'
    },
    manualInput: {
      padding: '0.5rem 0.75rem',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      fontSize: '0.9rem',
      fontWeight: '600',
      color: '#374151',
      backgroundColor: 'white',
      width: '120px',
      textAlign: 'center',
      outline: 'none',
      transition: 'border-color 0.2s ease'
    },
    sliderContainer: {
      position: 'relative',
      margin: '1rem 0'
    },
    slider: {
      width: '100%',
      height: '6px',
      borderRadius: '3px',
      background: '#e5e7eb',
      outline: 'none',
      WebkitAppearance: 'none',
      appearance: 'none'
    },
    sliderLabels: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '0.75rem',
      color: '#9ca3af',
      marginTop: '0.5rem'
    },
    calculateButton: {
      backgroundColor: '#22c55e',
      color: 'white',
      border: 'none',
      padding: '0.75rem 2rem',
      borderRadius: '25px',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      alignSelf: 'center',
      marginTop: '1rem'
    },
    resultSection: {
      marginTop: '3rem',
      padding: '2rem',
      backgroundColor: '#f8f9fa',
      borderRadius: '15px',
      border: '1px solid #e5e7eb'
    },
    chartSection: {
      marginBottom: '2rem'
    },
    chartTitle: {
      fontSize: '1.25rem',
      fontWeight: '700',
      color: '#1a1a1a',
      marginBottom: '1rem',
      textAlign: 'center'
    },
    chartContainer: {
      position: 'relative',
      height: '400px',
      backgroundColor: 'white',
      borderRadius: '10px',
      border: '1px solid #e5e7eb',
      padding: '20px',
      marginBottom: '2rem'
    },
    resultGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr 1fr 1fr',
      gap: '1rem',
      textAlign: 'center'
    },
    resultItem: {
      padding: '1.5rem',
      backgroundColor: 'white',
      borderRadius: '12px',
      border: '1px solid #e5e7eb'
    },
    resultLabel: {
      fontSize: '0.9rem',
      color: '#6b7280',
      marginBottom: '0.5rem',
      fontWeight: '500'
    },
    resultValue: {
      fontSize: '1.3rem',
      fontWeight: '700',
      color: '#1a1a1a'
    },
    resultText: {
      backgroundColor: '#e3f2fd',
      padding: '1.5rem',
      borderRadius: '12px',
      fontSize: '1rem',
      lineHeight: '1.6',
      color: '#1a1a1a',
      marginTop: '1rem'
    },
    resultNote: {
      fontSize: '0.85rem',
      color: '#6b7280',
      fontStyle: 'italic',
      marginTop: '1rem'
    }
  };

  // SVG Chart Component
  const WithdrawalChart = ({ data }) => {
    if (!data || data.length === 0) return null;

    const maxValue = Math.max(initialInvestment, ...data.map(d => Math.max(d.remaining, d.withdrawn)));
    const chartWidth = 760;
    const chartHeight = 300;
    const padding = 60;
    const plotWidth = chartWidth - padding * 2;
    const plotHeight = chartHeight - padding * 2;

    const maxYear = Math.max(...data.map(d => d.year));
    const xScale = (year) => padding + (year / maxYear) * plotWidth;
    const yScale = (value) => chartHeight - padding - (value / maxValue) * plotHeight;

    // Create path for remaining amount
    const remainingLine = data.map((d, i) => 
      `${i === 0 ? 'M' : 'L'} ${xScale(d.year)} ${yScale(d.remaining)}`
    ).join(' ');

    // Create path for withdrawn amount
    const withdrawnLine = data.map((d, i) => 
      `${i === 0 ? 'M' : 'L'} ${xScale(d.year)} ${yScale(d.withdrawn)}`
    ).join(' ');

    // Create area under remaining amount
    const remainingArea = data.map((d, i) => 
      `${i === 0 ? 'M' : 'L'} ${xScale(d.year)} ${yScale(d.remaining)}`
    ).join(' ') + ` L ${xScale(maxYear)} ${chartHeight - padding} L ${padding} ${chartHeight - padding} Z`;

    // Generate Y-axis labels
    const yLabels = [];
    for (let i = 0; i <= 6; i++) {
      const value = (maxValue / 6) * i;
      yLabels.push({
        value: Math.round(value),
        y: yScale(value)
      });
    }

    return (
      <svg width="100%" height="400" viewBox={`0 0 ${chartWidth} ${chartHeight}`} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: 'white' }}>
        {/* Grid lines */}
        <defs>
          <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
            <path d="M 10 0 L 0 0 0 10" fill="none" stroke="#f3f4f6" strokeWidth="1"/>
          </pattern>
        </defs>
        <rect width={chartWidth} height={chartHeight} fill="url(#grid)" />
        
        {/* Y-axis labels */}
        {yLabels.map((label, i) => (
          <g key={i}>
            <line x1={padding} y1={label.y} x2={chartWidth - padding} y2={label.y} stroke="#e5e7eb" strokeWidth="1" />
            <text x={padding - 10} y={label.y + 5} textAnchor="end" fontSize="12" fill="#6b7280">
              {(label.value / 100000).toFixed(1)}L
            </text>
          </g>
        ))}
        
        {/* X-axis labels */}
        {data.map((d, i) => (
          <g key={i}>
            {i % 2 === 0 && (
              <>
                <line x1={xScale(d.year)} y1={chartHeight - padding} x2={xScale(d.year)} y2={chartHeight - padding + 5} stroke="#6b7280" strokeWidth="1" />
                <text x={xScale(d.year)} y={chartHeight - padding + 20} textAnchor="middle" fontSize="12" fill="#6b7280">
                  {d.year}
                </text>
              </>
            )}
          </g>
        ))}
        
        {/* Remaining amount area */}
        <path d={remainingArea} fill="#059669" opacity="0.3" />
        
        {/* Remaining amount line */}
        <path d={remainingLine} fill="none" stroke="#059669" strokeWidth="3" />
        
        {/* Withdrawn amount line */}
        <path d={withdrawnLine} fill="none" stroke="#8b5cf6" strokeWidth="2" />
        
        {/* Axis labels */}
        <text x={chartWidth / 2} y={chartHeight - 10} textAnchor="middle" fontSize="14" fontWeight="600" fill="#374151">
          Period in Years
        </text>
        <text x={25} y={chartHeight / 2} textAnchor="middle" fontSize="14" fontWeight="600" fill="#374151" transform={`rotate(-90, 25, ${chartHeight / 2})`}>
          Amount (Rs.)
        </text>
        
        {/* Legend */}
        <g transform={`translate(${chartWidth - 200}, 30)`}>
          <rect x="0" y="0" width="15" height="15" fill="#ef4444" />
          <text x="20" y="12" fontSize="12" fill="#374151">Remaining Amount</text>
          <rect x="0" y="25" width="15" height="15" fill="#8b5cf6" />
          <text x="20" y="37" fontSize="12" fill="#374151">Total Withdrawn</text>
        </g>
      </svg>
    );
  };

  return (
    <>
      <style>{`
        .swp-slider::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #5a68cfff;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        
        .swp-slider::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #5a68cfff;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        
        .swp-calculate-btn:hover {
          background-color: rgb(38, 220, 68) !important;
        }
        
        .manual-input:focus {
          border-color: #3b82f6 !important;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
        }
      `}</style>
      
      <div style={styles.swpCalculatorContent}>
        <p style={styles.description}>
          Calculate how long your investment will last with regular monthly withdrawals and see the impact on your corpus.
        </p>
        
        {/* Initial Investment */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Initial Investment (Rs.)
            <input
              type="number"
              min="100000"
              max="10000000"
              step="50000"
              value={initialInvestment}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setInitialInvestment('');
                } else {
                  const numValue = parseInt(inputValue);
                  if (!isNaN(numValue)) {
                    setInitialInvestment(Math.max(100000, Math.min(10000000, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                  setInitialInvestment(2000000);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter amount"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="100000"
              max="10000000"
              step="50000"
              value={initialInvestment}
              onChange={(e) => setInitialInvestment(parseInt(e.target.value))}
              style={styles.slider}
              className="swp-slider"
            />
            <div style={styles.sliderLabels}>
              <span>1L</span>
              <span>2.5L</span>
              <span>5L</span>
              <span>7.5L</span>
              <span>1Cr</span>
            </div>
          </div>
        </div>

        {/* Monthly Withdrawal */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Monthly Withdrawal (Rs.)
            <input
              type="number"
              min="1000"
              max="100000"
              step="1000"
              value={monthlyWithdrawal}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setMonthlyWithdrawal('');
                } else {
                  const numValue = parseInt(inputValue);
                  if (!isNaN(numValue)) {
                    setMonthlyWithdrawal(Math.max(1000, Math.min(100000, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                  setMonthlyWithdrawal(15000);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter amount"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="1000"
              max="100000"
              step="1000"
              value={monthlyWithdrawal}
              onChange={(e) => setMonthlyWithdrawal(parseInt(e.target.value))}
              style={styles.slider}
              className="swp-slider"
            />
            <div style={styles.sliderLabels}>
              <span>1K</span>
              <span>25K</span>
              <span>50K</span>
              <span>75K</span>
              <span>1L</span>
            </div>
          </div>
        </div>

        {/* Expected Rate of Return */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Expected rate of return (% p.a)
            <input
              type="number"
              min="1"
              max="20"
              step="0.1"
              value={expectedReturn}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setExpectedReturn('');
                } else {
                  const numValue = parseFloat(inputValue);
                  if (!isNaN(numValue)) {
                    setExpectedReturn(Math.max(1, Math.min(20, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseFloat(e.target.value))) {
                  setExpectedReturn(12);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter rate"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="1"
              max="20"
              step="0.1"
              value={expectedReturn}
              onChange={(e) => setExpectedReturn(parseFloat(e.target.value))}
              style={styles.slider}
              className="swp-slider"
            />
            <div style={styles.sliderLabels}>
              <span>1</span>
              <span>5</span>
              <span>10</span>
              <span>15</span>
              <span>20</span>
            </div>
          </div>
        </div>

        {/* Calculate Button */}
        <button
          style={styles.calculateButton}
          className="swp-calculate-btn"
          onClick={calculateSWP}
        >
          Calculate
        </button>

        {/* Results */}
        {result && (
          <div ref={resultRef} style={styles.resultSection}>
            {/* Withdrawal Chart */}
            <div style={styles.chartSection}>
              <h3 style={styles.chartTitle}>Withdrawal Chart</h3>
              <WithdrawalChart data={chartData} />
            </div>

            {/* Result Summary */}
            <div style={styles.resultGrid}>
              <div style={styles.resultItem}>
                <div style={styles.resultLabel}>Initial Investment</div>
                <div style={styles.resultValue}>₹{initialInvestment.toLocaleString()}</div>
              </div>
              <div style={styles.resultItem}>
                <div style={styles.resultLabel}>Duration</div>
                <div style={styles.resultValue}>{result.yearsLasted} Years</div>
              </div>
              <div style={styles.resultItem}>
                <div style={styles.resultLabel}>Total Withdrawn</div>
                <div style={styles.resultValue}>₹{result.totalWithdrawn.toLocaleString()}</div>
              </div>
              <div style={styles.resultItem}>
                <div style={styles.resultLabel}>Final Amount</div>
                <div style={styles.resultValue}>₹{result.finalAmount.toLocaleString()}</div>
              </div>
            </div>

            {/* Result Description */}
            <div style={styles.resultText}>
              With an initial investment of Rs. <strong>{initialInvestment.toLocaleString()}/-</strong> and monthly withdrawals of Rs. <strong>{monthlyWithdrawal.toLocaleString()}/-</strong>, 
              your investment will last for approximately <strong>{result.yearsLasted} years</strong> ({result.monthsLasted} months). 
              You will be able to withdraw a total of Rs. <strong>{result.totalWithdrawn.toLocaleString()}/-</strong> over this period.
              {result.finalAmount > 0 && (
                <> The remaining amount after this period will be Rs. <strong>{result.finalAmount.toLocaleString()}/-</strong>.</>
              )}
            </div>

            <div style={styles.resultNote}>
              Note: Based on monthly compounding. Actual returns may vary based on market conditions.
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SwpCalculatorSection;